import { currentMembership, resolveMembership, type Membership } from '../auth/household'
import { isSyncConfigured, supabase } from '../auth/supabase'
import { useAppStore } from '../store/store'
import { COLLECTION_NAMES, type CollectionName } from '../store/types'
import { formatSyncError } from './error'
import {
  latestTimestamp,
  mergeCollection,
  pendingRows,
  purgeTombstones,
  type Row,
} from './merge'
import {
  SETTINGS_FIELDS,
  SETTINGS_ROW_ID,
  SETTINGS_TABLE,
  TABLES,
  toIsoString,
  toLocal,
  toRemote,
} from './schema'
import { setSyncStatus } from './status'

/**
 * The sync driver, adapted from `../semainier/src/sync/sync.ts`.
 *
 * Three things are different, and all three follow from
 * `docs/architecture-review.md` §1.1:
 *
 *   - cursors are keyed by household, not by user — two people in one house
 *     must not each re-pull everything the other already had;
 *   - every payload carries `household_id`, since there is no `auth.uid()`
 *     default to fall back on;
 *   - there is no `adoptAccount`. It exists in semainier to stop a demo
 *     journal polluting a real account, and here the plan is code: a fresh
 *     install holds nothing, so there is nothing to adopt around.
 */

/**
 * Pull cursor per table, kept out of the synced payload.
 *
 * Keyed `householdId:table` — the scope the rows are keyed on, not the user
 * reading them. A cursor only ever moves forward, so anything a pull failed to
 * store is never asked for again; if a change ever makes stored cursors
 * untrustworthy, rename the key rather than trying to repair them. Every
 * install then re-adopts its household once, which is idempotent.
 */
const CURSOR_KEY = 'maison_zen_sync_cursors_v1'
const EPOCH = '1970-01-01T00:00:00.000Z'

type Cursors = Record<string, string>

function readCursors(): Cursors {
  try {
    return JSON.parse(localStorage.getItem(CURSOR_KEY) ?? '{}') as Cursors
  } catch {
    return {}
  }
}

function writeCursors(cursors: Cursors): void {
  try {
    localStorage.setItem(CURSOR_KEY, JSON.stringify(cursors))
  } catch {
    // A cursor that cannot be stored costs one re-pull, which is idempotent.
  }
}

function cursorKey(householdId: string, table: string): string {
  return `${householdId}:${table}`
}

/** Called on sign-out: the next account must not inherit this one's progress. */
export function resetCursors(): void {
  try {
    localStorage.removeItem(CURSOR_KEY)
  } catch {
    // Nothing to reset if storage is unavailable.
  }
}

// ------------------------------------------------------------------- paging

/**
 * Rows per pull request.
 *
 * A plain `select()` is capped by PostgREST's `max-rows` (1000 on a default
 * Supabase project). Silently returning the first thousand rows would be bad
 * enough on its own, but the cursor is then advanced to the newest row of that
 * page — so everything the cap hid is older than the cursor and is never asked
 * for again. The symptom is a green badge over a month view with checkmarks
 * missing. Hence: an explicit order, an explicit page size, and a loop.
 */
const PAGE_SIZE = 500

async function pullPages(
  table: string,
  householdId: string,
  since: string,
): Promise<Record<string, unknown>[]> {
  const client = supabase!
  const rows: Record<string, unknown>[] = []

  for (let offset = 0; ; offset += PAGE_SIZE) {
    const { data, error } = await client
      .from(table)
      .select('*')
      // RLS already limits this to the households the caller belongs to, but
      // "belongs to" is not "is looking at": a person in two households would
      // otherwise merge both into one local store.
      .eq('household_id', householdId)
      .gt('updated_at', since)
      // A whole upsert shares one transaction timestamp, so `updated_at` alone
      // is not a total order and pages would overlap or skip without `id`.
      .order('updated_at', { ascending: true })
      .order('id', { ascending: true })
      .range(offset, offset + PAGE_SIZE - 1)

    if (error) throw error

    const page = (data ?? []) as Record<string, unknown>[]
    rows.push(...page)
    if (page.length < PAGE_SIZE) return rows
  }
}

// ---------------------------------------------------------------- one table

async function pushCollection(name: CollectionName, householdId: string): Promise<void> {
  const client = supabase!
  const spec = TABLES[name]

  const local = useAppStore.getState()[name] as unknown as Row[]
  const dirty = pendingRows(local)
  if (!dirty.length) return

  const payload = dirty.map((r) =>
    toRemote(spec.fields, r as unknown as Record<string, unknown>, householdId),
  )
  const { data, error } = await client
    .from(spec.table)
    .upsert(payload, { onConflict: 'household_id,id' })
    .select()

  if (error) throw error

  // Adopt the server's updated_at so both devices compare the same clock.
  const accepted = new Map((data ?? []).map((r: Record<string, unknown>) => [r['id'] as string, r]))
  useAppStore.setState((s) => ({
    [name]: (s[name] as unknown as Row[]).map((row) => {
      const server = accepted.get(row.id)
      if (!server) return row
      return { ...row, updatedAt: toIsoString(server['updated_at']) ?? row.updatedAt, dirty: false }
    }),
  }) as never)
}

async function pullCollection(name: CollectionName, householdId: string, cursors: Cursors): Promise<void> {
  const spec = TABLES[name]
  const key = cursorKey(householdId, spec.table)
  const since = cursors[key] ?? EPOCH

  const remoteRows = await pullPages(spec.table, householdId, since)
  const incoming = remoteRows.map((r) => toLocal(spec.fields, r) as unknown as Row)

  const current = useAppStore.getState()[name] as unknown as Row[]
  const merged = mergeCollection(current, incoming)

  // Only write when the pull actually changed something. `startSync` schedules
  // a sync on every store mutation, so putting an identical array back would
  // leave the app syncing every two seconds for as long as it is open — a real
  // cost on a phone, and completely invisible from a desk.
  if (merged.applied > 0) {
    useAppStore.setState({ [name]: purgeTombstones(merged.rows) } as never)
  }

  cursors[key] = latestTimestamp(incoming, since)
}

// -------------------------------------------------------------- settings row

async function pushSettings(householdId: string): Promise<void> {
  const client = supabase!
  const local = useAppStore.getState().settings
  if (!local.dirty) return

  const { data, error } = await client
    .from(SETTINGS_TABLE)
    .upsert(
      toRemote(
        SETTINGS_FIELDS,
        { ...(local as unknown as Record<string, unknown>), id: SETTINGS_ROW_ID },
        householdId,
      ),
      { onConflict: 'household_id,id' },
    )
    .select()
    .single()

  if (error) throw error

  useAppStore.setState((s) => ({
    settings: {
      ...s.settings,
      updatedAt: toIsoString(data['updated_at']) ?? s.settings.updatedAt,
      dirty: false,
    },
  }))
}

async function pullSettings(householdId: string, cursors: Cursors): Promise<void> {
  const key = cursorKey(householdId, SETTINGS_TABLE)
  const since = cursors[key] ?? EPOCH

  // Not `maybeSingle()`: the household is supposed to hold exactly one settings
  // row, but if it ever holds two that would fail the whole sync rather than
  // just picking the newer one.
  const rows = await pullPages(SETTINGS_TABLE, householdId, since)
  const remote = rows[rows.length - 1]
  if (!remote) return

  const incoming = toLocal(SETTINGS_FIELDS, remote)
  const incomingAt = incoming['updatedAt'] as string
  const current = useAppStore.getState().settings

  if (incomingAt > current.updatedAt) {
    useAppStore.setState({
      settings: {
        reminderEnabled: Boolean(incoming['reminderEnabled']),
        reminderTime: String(incoming['reminderTime'] ?? current.reminderTime).slice(0, 5),
        timezone: String(incoming['timezone'] ?? current.timezone),
        updatedAt: incomingAt,
        deletedAt: null,
        dirty: false,
      },
    })
  }

  cursors[key] = incomingAt || since
}

// ------------------------------------------------------------------ driver

function countPending(): number {
  const state = useAppStore.getState()
  let n = state.settings.dirty ? 1 : 0
  for (const name of COLLECTION_NAMES) {
    n += (state[name] as unknown as Row[]).filter((r) => r.dirty).length
  }
  return n
}

let running = false
let queued = false

/**
 * Push local changes, then pull remote ones, for the settings row and every
 * collection.
 *
 * Serialised: a second call while one is in flight sets a flag and runs once
 * afterwards, rather than interleaving two passes over the same tables.
 */
export async function syncNow(): Promise<void> {
  if (!isSyncConfigured || !supabase) {
    setSyncStatus({ phase: 'disabled', pending: 0 })
    return
  }

  if (running) {
    queued = true
    return
  }

  // getSession reads the stored session and refreshes it only when it has
  // actually expired. getUser, which the draft effectively called on every
  // push, always went to /auth/v1/user — a network round trip in front of
  // every sync, and the source of the "JWT expired" failure a phone reports
  // when it wakes up with a stale access token.
  const { data: auth } = await supabase.auth.getSession()
  if (!auth.session?.user?.id) {
    setSyncStatus({ phase: 'signed-out', pending: countPending() })
    return
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    setSyncStatus({ phase: 'offline', pending: countPending() })
    return
  }

  // Normally `AuthGate` has already resolved this. Doing it here too covers the
  // device that signed in with no network and reached the app on a cached
  // session: the household is what every row is scoped to, so there is nothing
  // to sync until it is known.
  let membership: Membership
  try {
    membership = currentMembership() ?? (await resolveMembership())
  } catch (error) {
    setSyncStatus({
      phase: 'no-household',
      pending: countPending(),
      lastError: formatSyncError(error),
    })
    return
  }

  running = true
  setSyncStatus({ phase: 'syncing', lastError: null })

  try {
    const cursors = readCursors()
    const { householdId } = membership

    await pushSettings(householdId)
    await pullSettings(householdId, cursors)

    for (const name of COLLECTION_NAMES) {
      await pushCollection(name, householdId)
      await pullCollection(name, householdId, cursors)
    }

    // Written only after a fully successful pass: a cursor that moved past rows
    // a failed pull never stored could never be walked back.
    writeCursors(cursors)
    setSyncStatus({
      phase: 'idle',
      pending: countPending(),
      lastSyncAt: new Date().toISOString(),
      lastError: null,
    })
  } catch (error) {
    // Never destructive: a failed sync leaves local rows and their dirty flags
    // exactly as they were, so the next attempt retries the same changes.
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false
    setSyncStatus({
      phase: offline ? 'offline' : 'error',
      pending: countPending(),
      lastError: formatSyncError(error),
    })
  } finally {
    running = false
    if (queued) {
      queued = false
      void syncNow()
    }
  }
}

/**
 * Forget every cursor and pull the whole household again.
 *
 * The escape hatch for a device showing a green badge over a month that is
 * missing checkmarks — otherwise unrecoverable, since a cursor only moves
 * forward. Non-destructive: pending local changes stay dirty and still push.
 */
export async function resyncFromScratch(): Promise<void> {
  resetCursors()
  await syncNow()
}

/** Debounced trigger for "something changed, sync soon". */
let debounce: ReturnType<typeof setTimeout> | undefined

export function scheduleSync(delayMs = 2000): void {
  clearTimeout(debounce)
  debounce = setTimeout(() => void syncNow(), delayMs)
}

let started = false

/**
 * Wire the triggers: startup, reconnection, tab focus, any local mutation, and
 * a slow interval as a backstop for all of the above failing.
 *
 * Nothing else may call `syncNow()` on a schedule of its own — the point of
 * routing every trigger through here is that they all land on one serialised
 * pass.
 */
export function startSync(): () => void {
  if (started || !isSyncConfigured) return () => undefined
  started = true

  const onOnline = () => void syncNow()
  const onOffline = () => setSyncStatus({ phase: 'offline', pending: countPending() })
  const onVisible = () => {
    if (document.visibilityState === 'visible') scheduleSync(200)
  }

  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
  document.addEventListener('visibilitychange', onVisible)

  const unsubscribe = useAppStore.subscribe(() => {
    setSyncStatus({ pending: countPending() })
    scheduleSync()
  })

  const interval = setInterval(() => void syncNow(), 5 * 60 * 1000)

  void syncNow()

  return () => {
    started = false
    window.removeEventListener('online', onOnline)
    window.removeEventListener('offline', onOffline)
    document.removeEventListener('visibilitychange', onVisible)
    unsubscribe()
    clearInterval(interval)
    clearTimeout(debounce)
  }
}
